import React, { useState, useEffect } from 'react';
import { useWeb3 } from '../context/Web3Context';
import { useLanguage } from '../i18n/LanguageContext';
import { withTxToast } from '../utils/txToast';
import { ShoppingCart, Loader, RefreshCw, Tag, ExternalLink } from 'lucide-react';

const NFTMarketplaceComponent = ({ nftContract, marketplaceContract, nftContractAddress, marketplaceContractAddress, refreshSignal, onRefresh }) => {
  const { web3, account, contract } = useWeb3();
  const { t } = useLanguage();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [buying, setBuying] = useState(null);

  useEffect(() => {
    loadListings();
  }, [marketplaceContract, nftContract, refreshSignal]);

  const loadListings = async () => {
    if (!marketplaceContract || !nftContract || !web3) return;

    setLoading(true);
    try {
      const nextListingId = Number(await marketplaceContract.methods.nextListingId().call());
      const ids = Array.from({ length: nextListingId - 1 }, (_, i) => i + 1);

      const settled = await Promise.allSettled(
        ids.map(async (i) => {
          try {
            const listing = await marketplaceContract.methods.getListing(i).call();
            if (!listing.active || listing.nftContract.toLowerCase() !== nftContractAddress.toLowerCase()) return null;
            const [uri, creator] = await Promise.all([
              nftContract.methods.tokenURI(listing.tokenId).call(),
              nftContract.methods.getTokenCreator(listing.tokenId).call(),
            ]);
            return {
              listingId: i,
              seller: listing.seller,
              tokenId: listing.tokenId,
              price: web3.utils.fromWei(listing.price, 'ether'),
              uri,
              creator,
            };
          } catch {
            return null;
          }
        })
      );

      setListings(settled.filter(r => r.status === 'fulfilled' && r.value !== null).map(r => r.value));
    } catch (err) {
      console.error('Error loading listings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBuyNFT = async (listingId, price) => {
    if (!marketplaceContract || !account || !web3 || !contract) {
      console.warn('[BuyNFT] Faltan dependencias:', { marketplaceContract: !!marketplaceContract, account, web3: !!web3, contract: !!contract });
      return;
    }
    if (!marketplaceContractAddress) {
      console.error('[BuyNFT] marketplaceContractAddress no definido');
      return;
    }

    setBuying(listingId);
    try {
      const priceWei = web3.utils.toWei(price.toString(), 'ether');
      console.log('[BuyNFT] listingId:', listingId, '| precio BFPE:', price, '| priceWei:', priceWei);

      // Paso 1: aprobar el marketplace para gastar BFPE (si no hay allowance suficiente)
      const currentAllowance = await contract.methods.allowance(account, marketplaceContractAddress).call();
      console.log('[BuyNFT] allowance actual:', currentAllowance.toString(), '| necesario:', priceWei);

      if (BigInt(currentAllowance) < BigInt(priceWei)) {
        await withTxToast(
          () => contract.methods.approve(marketplaceContractAddress, priceWei).send({ from: account }),
          t('marketplace.approveLoading'),
          t('marketplace.approveSuccess')
        );
        console.log('[BuyNFT] Approve BFPE completado');
      } else {
        console.log('[BuyNFT] Allowance suficiente, saltando approve');
      }

      // Paso 2: comprar el NFT
      const receipt = await withTxToast(
        () => marketplaceContract.methods.buyNFT(listingId).send({ from: account, gas: 500000 }),
        t('marketplace.buyLoading'),
        t('marketplace.buySuccess')
      );
      console.log('[BuyNFT] Compra completada, tx:', receipt?.transactionHash);

      await loadListings();
      onRefresh?.();
    } catch (err) {
      console.error('[BuyNFT] Error:', err?.message || err);
    } finally {
      setBuying(null);
    }
  };

  if (!account) {
    return (
      <div className="card">
        <div className="flex items-center space-x-2 mb-4">
          <ShoppingCart className="w-6 h-6 text-purple-600" />
          <h2 className="text-2xl font-bold text-gray-800">{t('marketplace.title')}</h2>
        </div>
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            {t('marketplace.noWallet')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <ShoppingCart className="w-6 h-6 text-purple-600" />
          <h2 className="text-2xl font-bold text-gray-800">{t('marketplace.title')}</h2>
        </div>
        <button
          onClick={loadListings}
          disabled={loading}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <RefreshCw className={`w-5 h-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-8">
          <Loader className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      ) : listings.length === 0 ? (
        <div className="p-8 text-center bg-gray-50 rounded-lg">
          <ShoppingCart className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">{t('marketplace.empty')}</p>
          <p className="text-sm text-gray-500 mt-1">{t('marketplace.emptySubtext')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {listings.map((listing) => (
            <div key={listing.listingId} className="border border-gray-200 rounded-lg p-4 hover:shadow-lg transition-shadow">
              <div className="mb-3">
                <h3 className="font-bold text-lg text-gray-800">NFT #{listing.tokenId}</h3>
                <p className="text-xs text-gray-500">
                  Seller: {listing.seller.substring(0, 6)}...{listing.seller.substring(38)}
                </p>
                <p className="text-xs text-gray-500">
                  Creator: {listing.creator.substring(0, 6)}...{listing.creator.substring(38)}
                </p>
              </div>

              <div className="mb-3">
                <p className="text-xs text-gray-600 mb-1">Token URI:</p>
                <a
                  href={listing.uri}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:underline flex items-center space-x-1"
                >
                  <span className="truncate">{listing.uri.substring(0, 25)}...</span>
                  <ExternalLink className="w-3 h-3 flex-shrink-0" />
                </a>
              </div>

              <div className="flex items-center justify-between mb-3 p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg">
                <span className="text-sm font-medium text-gray-600">{t('marketplace.priceLabel')}</span>
                <span className="text-xl font-bold text-purple-600">{listing.price} BFPE</span>
              </div>

              {listing.seller.toLowerCase() === account.toLowerCase() ? (
                <div className="text-center py-2 bg-gray-100 rounded-lg">
                  <p className="text-sm text-gray-600">{t('marketplace.yourNFT')}</p>
                </div>
              ) : (
                <button
                  onClick={() => handleBuyNFT(listing.listingId, listing.price)}
                  disabled={buying === listing.listingId}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {buying === listing.listingId ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      <span>{t('marketplace.buyingBtn')}</span>
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="w-4 h-4" />
                      <span>{t('marketplace.buyBtn')}</span>
                    </>
                  )}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NFTMarketplaceComponent;
